<template>
  <card-container
    :id="pageId"
    loading-message="Requesting Home Services..."
    :is-loading="isLoading"
    :disabled="isSubmitDisabled"
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      New Home Service Request
    </template>

    <template #content>
      <h6 class="sub-section-title">
        Caller Information
      </h6>

      <div class="row align-items-center">
        <div class="col-6">
          <label class="control-label"> Phone Number* </label>
          <phone-number-input
            v-model="phoneNumber"
            :disabled="isFamilyDashboard"
            required
          />
        </div>
      </div>

      <h6 class="sub-section-title">
        Request Home Services
      </h6>

      <div class="row">
        <div class="pl-3 pb-2 text-black-50">
          Select all services the caller is requesting. A separate service order will be created for each.
        </div>
        <div class="col-sm-12">
          <el-checkbox-group v-model="serviceTypes">
            <div class="row mb-0">
              <div
                v-for="(item, index) in availableServiceTypeListToCreateUpdate"
                :key="index"
                class="col-sm-4 checkbox-container"
              >
                <el-checkbox :label="item.key">
                  {{ item.label }}
                </el-checkbox>
              </div>
            </div>
          </el-checkbox-group>
        </div>
      </div>

      <div class="row mt-2 mb-2">
        <div class="col-6">
          <label class="control-label"> How did you find out about Home Services? </label>
          <dropdown-list
            v-model="hearAboutUs"
            :options="homeServiceHearAboutUsList"
            clearable
          />
        </div>

        <div
          v-if="isOtherHearAboutUsFieldShow"
          class="col-sm-6"
        >
          <label class="control-label"> Please Specify* </label>
          <sanitized-input
            id="otherHearAboutUs"
            v-model="otherHearAboutUs"
            required
          />
        </div>
      </div>

      <template v-if="serviceTypes.length > 0">
        <h6 class="sub-section-title">
          Service Details
        </h6>

        <HomeServiceRequestDetailItem
          v-for="(type, index) in serviceTypes"
          :key="index"
          v-model="serviceDetails[type]"
          :disabled-date="disabledDate"
          :caller-time-zone="callerTimeZone"
        >
          <template #title>
            {{ startCase(type) }}
          </template>
        </HomeServiceRequestDetailItem>
      </template>
    </template>

    <template #submit-button>
      Create Service Order
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, watch, computed, defineAsyncComponent } from 'vue';
import moment from 'moment-timezone';
import { startCase, debounce } from 'lodash';

import util from '@/Utils/util';
import { catchErrorMessageBox } from '@/Utils/catchErrorMessageBox';
import { useDashboard } from '@/use/app';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { useHomeService } from '@/use/homeService';
import { type HomeServiceOrderInput, HomeServiceFrequency, HomeServiceType } from '@/models/homeService';
import { homeServiceOrderService } from '@/services/homeServiceOrder';
import { callerService } from '@/services/caller';
import { homeServiceHearAboutUsList } from '@/constants/homeService';

const HomeServiceRequestDetailItem = defineAsyncComponent(() => import('./Order/HomeServiceRequestDetailItem.vue') as any);

const pageId = 'request-home-services';

const props = defineProps<{
  defaultPhoneNumber?: string;
}>();

const { isFamilyDashboard } = useDashboard();
const { customerPhoneNumber, customerInfo } = useCustomer();

const { showSuccessPopupDialog } = useSwalDialog();
const { availableServiceTypeListToCreateUpdate } = useHomeService();

const phoneNumber = ref('');
const serviceTypes = ref([]);
const serviceDetails: Ref<{
  [key: string]: HomeServiceOrderInput;
}> = ref({});
const hearAboutUs = ref('');
const otherHearAboutUs = ref('');

const isLoading = ref(false);

const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(phoneNumber.value));

const callerTimeZone: Ref<string | null> = ref(null);

const loadCallerTimeZone = debounce(async (value: string) => {
  callerTimeZone.value = util.isPhoneNumberValid(value) ? await callerService.fetchCallerTimeZone(value) : null;
}, 500);

watch(phoneNumber, (value) => {
  callerTimeZone.value = null;
  loadCallerTimeZone(value);
}, { immediate: true });

// The pickers work with wall-clock time in the caller's timezone; the backend stores UTC.
const toUtcJobScheduledAt = (picked?: Date | string | null) => {
  if (!picked) {
    return null;
  }

  const wallTime = moment(picked).format('YYYY-MM-DD HH:mm');
  const utcValue = callerTimeZone.value
    ? moment.tz(wallTime, 'YYYY-MM-DD HH:mm', callerTimeZone.value)
    : moment.utc(wallTime, 'YYYY-MM-DD HH:mm');

  return utcValue.toISOString();
};
const isSubmitDisabled = computed(() => {
  if (!isPhoneNumberValid.value) {
    return true;
  }

  if (serviceTypes.value.length === 0) {
    return true;
  }

  if (isLoading.value) {
    return true;
  }

  return false;
});
const isOtherHearAboutUsFieldShow = computed(() => hearAboutUs.value === 'Other');

const disabledDate = (date: Date) => {
  return moment(date).isBefore(moment(), 'day');
};

const submitForm = async () => {
  catchErrorMessageBox(async () => {
    isLoading.value = true;

    const input: HomeServiceOrderInput[] = [];
    for (const serviceType of serviceTypes.value) {
      if (!serviceDetails.value[serviceType]) {
        continue;
      }

      input.push({
        phoneNumber: phoneNumber.value,
        serviceType,
        frequency: serviceDetails.value[serviceType].frequency,
        budget: Number(serviceDetails.value[serviceType].budget),
        jobScheduledAt: toUtcJobScheduledAt(serviceDetails.value[serviceType].jobScheduledAt),
        orderNotes: serviceDetails.value[serviceType].orderNotes,
        hearAboutUs: isOtherHearAboutUsFieldShow.value ? otherHearAboutUs.value.trim() : hearAboutUs.value,
      });
    }

    await homeServiceOrderService.createHomeServiceOrders(input);

    showSuccessPopupDialog({
      text: 'New service request has been created successfully!',
    });

    resetForm();
  })
    .finally(() => (isLoading.value = false))
    .catch(() => null);
};

const resetForm = () => {
  phoneNumber.value = props.defaultPhoneNumber ?? customerPhoneNumber.value ?? '';
  serviceTypes.value = [];
  serviceDetails.value = {};
  hearAboutUs.value = '';
};

watch(customerInfo, resetForm, {
  immediate: true,
});

watch(serviceTypes, (newValue: string[]) => {
  for (const type of newValue) {
    if (serviceDetails.value[type]) {
      continue;
    }

    serviceDetails.value[type] = {
      frequency: HomeServiceFrequency.OneTime,
    };
  }
});
</script>

<style lang="scss">
#request-home-services {
  input[type='number'] {
    max-width: unset;
  }
}
</style>
